import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Box } from '@mui/material';
import { NavigateFunction, useNavigate, useLocation } from 'react-router-dom';

// COMPONENT 👇
import * as styles from '.././styles/login.style'
import { RouteImage } from '../../../assets/routeImage';
import InputTextField from '../../../component/input/inputTextField';
import { ButtonCustom } from '../../../component/mui-custom/MuiCustom';

// ICONS
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Loading from '../../../component/loading/Loading';
import { Authentication } from '../../../api/auth/Authentication.api';
import { swalSuccess } from '../../../component/notification/swal';
import { ResourcePathApi } from '../../../constants/resource.pathapi';

type resetPassword = {
    password: string;
    confirmPassword: string;
}
type errorMessage = {
    password: string;
    confirmPassword: string;
}

const ResetPassword: React.FC = () => {
    const { t } = useTranslation();
    const navigate: NavigateFunction = useNavigate();
    const location = useLocation();
    const token: string = location.state?.token || '';
    const [pathUrl, setPathUrl] = useState<any>(null);

    // STATE
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isShowPassword, setIsShowPassword] = useState<boolean>(false)
    const [isShowConfirmPassword, setIsShowConfirmPassword] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<errorMessage>({
        password: "",
        confirmPassword: ""
    })
    const [formState, setFormState] = useState<resetPassword>({
        password: "",
        confirmPassword: ""
    });
    const handleTogglePassword = () => setIsShowPassword(!isShowPassword)
    const handleToggleConfirmPassword = () => setIsShowConfirmPassword(!isShowConfirmPassword)

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (formState.password.length < 4) {
            setErrorMessage({ ...errorMessage, password: "โปรดระบุตัวอักษรภาษาอังกฤษและตัวเลขอย่างน้อยไป 8 ตัว" })
        } else if (formState.password !== formState.confirmPassword) {
            setErrorMessage({ ...errorMessage, confirmPassword: "รหัสผ่านไม่ตรงกัน" })
        }
        else {
            setIsLoading(true)
            setErrorMessage({ ...errorMessage, password: "", confirmPassword: "" })
            await Authentication.password(formState.password, token)
                .then(res => {
                    if (res.status === 200) {
                        setIsLoading(false)
                        swalSuccess("บันทึกข้อมูลเรียบร้อย")
                        window.location.href = '/login'
                    }
                })
                .catch(error => {
                    setIsLoading(false)
                    console.log(error)
                })
        }
    }

    useEffect(() => {
        if (formState.password.length >= 4 || formState.password === formState.confirmPassword) {
            setErrorMessage({ ...errorMessage, password: "", confirmPassword: "" })
        }
    }, [formState.password, formState.confirmPassword])

    useEffect(() => {
        location.state === null && navigate('/forgot-password')
    }, [])

    useEffect(() => {
        const fetchPath = async () => {
            const path = await ResourcePathApi();
            setPathUrl(path);
        }
        fetchPath();
    }, [])

    return (
        <styles.Container
            sx={{
                background: pathUrl && `url(${pathUrl.REACT_APP_BASE_API}/login-background) no-repeat`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            {isLoading && <Loading />}
            <styles.FormAuthentication className='w-full' onSubmit={handleSubmit}>
                <styles.MainBox>
                    <styles.LoginBox
                        style={{ minHeight: "716px" }}
                        className='border-[1px] border-light'
                        initial={{ x: 200 }}
                        animate={{ x: 0 }}
                        transition={{ type: "spring", stiffness: 100, ease: "backInOut", bounce: 0.25 }}
                    >
                        <div className='flex justify-center items-center'>
                            <img src={RouteImage.login_logo} alt='logo' />
                        </div>
                        <div className='mt-[36px]'>
                            <Typography variant='h4' component="h1">{t("ระบบสารสนเทศเพื่อการติดตามและประเมินผลโครงการ")}</Typography>
                            <Typography variant='h4' component="h1" >
                                {t(" (E-Monitoring and Evaluation)")}
                            </Typography>
                        </div>

                        <Box className="mt-9 flex justify-center items-center relative gap-x-3">
                            <span className='flex-1 h-[1px] bg-base_secondary'></span>
                            <span>
                                <Typography className='text-base_secondary' variant='h6'>
                                    {t("AUTH.CHANGE_PASSWORD")}
                                </Typography>
                            </span>
                            <span className='flex-1 h-[1px] bg-base_secondary'></span>
                        </Box>
                        <Box className="mt-9 flex flex-col gap-5">
                            <span>
                                <InputTextField
                                    required
                                    value={formState.password}
                                    onchange={(e) => setFormState({ ...formState, password: e.target.value })}
                                    heading='รหัสผ่าน'
                                    type={isShowPassword ? 'text' : 'password'}
                                    helperText={errorMessage.password}
                                    endAdornment={
                                        isShowPassword ?
                                            <VisibilityIcon onClick={handleTogglePassword} className='text-base_secondary' /> :
                                            <VisibilityOffIcon onClick={handleTogglePassword} className='text-base_secondary' />
                                    }
                                />
                            </span>
                            <span>
                                <InputTextField
                                    required
                                    value={formState.confirmPassword}
                                    onchange={(e) => setFormState({ ...formState, confirmPassword: e.target.value })}
                                    heading='ยืนยันรหัสผ่าน'
                                    type={isShowConfirmPassword ? 'text' : 'password'}
                                    helperText={errorMessage.confirmPassword}
                                    endAdornment={
                                        isShowConfirmPassword ?
                                            <VisibilityIcon onClick={handleToggleConfirmPassword} className='text-base_secondary' />
                                            : <VisibilityOffIcon onClick={handleToggleConfirmPassword} className='text-base_secondary' />
                                    }
                                />
                            </span>
                        </Box>
                        <Box className='w-full mt-[36px]'>
                            <ButtonCustom type='submit' variant='contained' sx={{ height: "48px" }} fullWidth>
                                {t("BUTTON.SUBMIT")}
                            </ButtonCustom>
                        </Box>
                    </styles.LoginBox>
                </styles.MainBox>
            </styles.FormAuthentication>
        </styles.Container>
    )
}

export default ResetPassword
